import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMyAttendanceHistory, type AttendanceRecord } from "../../services/api";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero, GameIcon, RingProgress, StatTile } from "../../components/game";
import type { GameIconName } from "../../components/game";

type AttendanceStatus = "present" | "late" | "absent" | "excused";
type FilterType = "all" | AttendanceStatus;

export default function AppAttendance() {
  const { t } = useTranslation();

  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getMyAttendanceHistory();
      setRecords(data);
    } catch (error) {
      console.error("Failed to load attendance:", error);
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const stats = {
    present: records.filter(r => r.status === "present").length,
    late: records.filter(r => r.status === "late").length,
    absent: records.filter(r => r.status === "absent").length,
    excused: records.filter(r => r.status === "excused").length,
  };

  const totalSessions = records.length;
  const attendanceRate = totalSessions > 0
    ? Math.round(((stats.present + stats.late) / totalSessions) * 100)
    : 0;

  const filteredRecords = filter === "all"
    ? records
    : records.filter(r => r.status === filter);

  const getStatusConfig = (status: string): { label: string; bg: string; text: string; icon: GameIconName } => {
    switch (status) {
      case "present":
        return { label: t("attendance.present", "Prezent"), bg: "bg-emerald-100", text: "text-emerald-700", icon: "check" };
      case "late":
        return { label: t("attendance.late", "Întârziat"), bg: "bg-amber-100", text: "text-amber-700", icon: "clock" };
      case "absent":
        return { label: t("attendance.absent", "Absent"), bg: "bg-red-100", text: "text-red-700", icon: "x" };
      case "excused":
        return { label: t("attendance.excused", "Motivat"), bg: "bg-slate-100", text: "text-slate-700", icon: "bulb" };
      default:
        return { label: status, bg: "bg-slate-100", text: "text-slate-700", icon: "star" };
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleFilter = (status: AttendanceStatus) =>
    setFilter(filter === status ? "all" : status);

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero className="rounded-br-3xl px-4 pt-6 pb-6">
          <h1 className="text-2xl font-extrabold">{t("attendance.title", "Istoricul prezențelor")}</h1>
          <p className="text-white/80 text-sm mt-1">{t("attendance.subtitle", "Vezi prezența ta la antrenamente")}</p>

          {/* Attendance Rate */}
          <div className="mt-5 flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">{t("attendance.rate", "Rata de prezență")}</p>
              <h2 className="text-4xl font-extrabold mt-1">{attendanceRate}%</h2>
              <p className="text-white/70 text-sm mt-1">
                {stats.present + stats.late} / {totalSessions} {t("attendance.sessions", "ședințe")}
              </p>
            </div>
            <RingProgress
              pct={attendanceRate}
              size={88}
              stroke={9}
              color="#ffffff"
              track="rgba(255,255,255,0.25)"
            >
              <span className="text-lg font-extrabold">{attendanceRate}%</span>
            </RingProgress>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-5">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2">
            <div className={filter === "present" ? "rounded-2xl ring-2 ring-emerald-400 ring-offset-1" : ""}>
              <StatTile icon="check" color="#10b981" value={stats.present} label={t("attendance.presentShort", "Prezent")} onClick={() => toggleFilter("present")} />
            </div>
            <div className={filter === "late" ? "rounded-2xl ring-2 ring-amber-400 ring-offset-1" : ""}>
              <StatTile icon="clock" color="#f59e0b" value={stats.late} label={t("attendance.lateShort", "Întârziat")} onClick={() => toggleFilter("late")} />
            </div>
            <div className={filter === "absent" ? "rounded-2xl ring-2 ring-red-400 ring-offset-1" : ""}>
              <StatTile icon="x" color="#ef4444" value={stats.absent} label={t("attendance.absentShort", "Absent")} onClick={() => toggleFilter("absent")} />
            </div>
            <div className={filter === "excused" ? "rounded-2xl ring-2 ring-slate-400 ring-offset-1" : ""}>
              <StatTile icon="bulb" color="#64748b" value={stats.excused} label={t("attendance.excusedShort", "Motivat")} onClick={() => toggleFilter("excused")} />
            </div>
          </div>

          {/* Records List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <GameCard className="p-6 text-center">
              <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
              <p className="text-sm text-slate-500">
                {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
              </p>
              <button
                onClick={loadData}
                className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
              >
                {t("common.retry", "Reîncearcă")}
              </button>
            </GameCard>
          ) : filteredRecords.length > 0 ? (
            <GameCard className="overflow-hidden divide-y divide-slate-100">
              {filteredRecords.map((record) => {
                const statusConfig = getStatusConfig(record.status);
                return (
                  <div key={record.id} className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Status Icon */}
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${statusConfig.bg} ${statusConfig.text}`}>
                        <GameIcon name={statusConfig.icon} className="w-5 h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h4 className="font-bold text-slate-800 truncate">{record.session.title}</h4>
                            <p className="text-sm text-slate-500">{record.session.team?.name}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}>
                            {statusConfig.label}
                          </span>
                        </div>

                        {/* Date & Time */}
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{formatDate(record.session.start_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <GameIcon name="clock" className="w-4 h-4" />
                            <span>{formatTime(record.session.start_at)}</span>
                          </div>
                        </div>

                        {/* Reason if provided */}
                        {record.reason && (
                          <p className="text-sm text-slate-500 mt-2 italic bg-slate-50 rounded-2xl px-3 py-2">
                            "{record.reason}"
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </GameCard>
          ) : (
            <GameCard className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                <GameIcon name="check" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                {filter === "all"
                  ? t("attendance.noRecords", "Niciun istoric")
                  : t("attendance.noFilteredRecords", "Niciun rezultat")
                }
              </h3>
              <p className="text-slate-500 text-sm">
                {filter === "all"
                  ? t("attendance.noRecordsDesc", "Prezențele tale vor apărea aici")
                  : t("attendance.noFilteredDesc", "Nu ai înregistrări cu acest status")
                }
              </p>
              {filter !== "all" && (
                <GameButton variant="ghost" className="mt-4 w-auto mx-auto px-4 py-2" onClick={() => setFilter("all")}>
                  {t("common.showAll", "Arată toate")}
                </GameButton>
              )}
            </GameCard>
          )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
